// ---------------------------------------------------------------------------
// Hours Breakdown - a one-off admin report.
//
// Pick one or more people and a date range, pull their TimeCamp entries live,
// and read the result. Several people make one combined report rather than a
// report each - the totals, both charts and every rollup cover the group, and
// the entry table grows an Employee column so a row can still be traced back.
//
// Every number, chart and row is derived from one set of checked tasks, so
// changing what counts re-totals the whole page without another round trip.
//
// Those checkboxes live in the collapsed Report Settings panel rather than on
// the page, because on all but a handful of visits the answer is "the same
// tasks as last time". The saved filter is per-viewer and is applied as the
// starting point for every report; the panel header always says how many tasks
// are being left out, so the filter is never silently in effect.
//
// REMOVING THIS PAGE - four places, all marked:
//   1. this file and HoursBreakdownPage.css
//   2. the route in client/src/App.js          (search "Hours Breakdown")
//   3. the tile in client/src/pages/AdminPage.jsx  (the 'hours-breakdown' item)
//   4. the fenced hook block in client/src/hooks/useQueries.js
// The Work Type section is the AI view: it reads each entry's description and
// sorts it into one of four kinds of work, deliberately not trusting the task it
// was logged against. It only runs when asked, only sends the tasks that are
// being counted, and caches its answers, so re-running costs almost nothing.
//
// Server side: server/routes/hoursBreakdown.js, server/config/hoursBreakdownBuckets.js,
// two lines in server/index.js, and both hours_breakdown_* tables.
//
// It borrows PiePanel from the R&D credits module rather than growing a second
// donut chart. The dependency runs one way - nothing in R&D credits knows this
// page exists - so removing this page leaves that module untouched.
// ---------------------------------------------------------------------------

import React, { useEffect, useMemo, useState } from 'react';
import { Button, ButtonGroup, CheckPicker, DateRangePicker, Input, Message, Placeholder, SelectPicker } from 'rsuite';
import { FilterField, FilterToolbar, SortableTh, sortRows, useTableSort } from '@nsi-advisory-services/components';

import PageHeader from '../components/shared/PageHeader';
import PayPeriodSelect from '../components/shared/PayPeriodSelect';
import Section from '../components/shared/Section';
import PiePanel from '../components/rndCredits/PiePanel';
import {
  useClassifyHoursBreakdown,
  useHoursBreakdown,
  useHoursBreakdownClassifyStatus,
  useHoursBreakdownEmployees,
  useHoursBreakdownSettings,
  useSaveHoursBreakdownSettings,
  useSetHoursBreakdownOverride,
} from '../hooks/useQueries';
import { useAuth } from '../contexts/AuthContext';
import {
  getCurrentPayPeriod,
  getPayPeriodOptions,
  toDateOnlyString,
  formatPeriodLabel,
} from '../utils/payPeriods';
import { getQuickDateRanges, getWeekOptions } from '../utils/dateRanges';
import { showToast } from '../utils/toast';
import { exportWorkTypePdf } from '../utils/hoursBreakdownExport';
import { renderEmployeeSelection } from '../components/shared/employeeSelection';
import './HoursBreakdownPage.css';

const fmtHours = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return (Math.round((n + Number.EPSILON) * 100) / 100).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  });
};

const fmtPct = (value) => `${(Math.round((Number(value) || 0) * 10) / 10)}%`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: '2-digit', weekday: 'short' });
};

const fmtPulledAt = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleTimeString();
};

// The label a task is known by everywhere on this page. Parent first, because a
// TimeCamp task name on its own ("Design", "Review") says very little.
const taskDisplay = (task) => {
  const name = task.taskName || (task.taskId && task.taskId !== '0' ? `Task ${task.taskId}` : 'No task');
  return task.parentTaskName ? `${task.parentTaskName} / ${name}` : name;
};

export default function HoursBreakdownPage() {
  const auth = useAuth();
  const employeesQuery = useHoursBreakdownEmployees();

  // --- Filters -------------------------------------------------------------

  const payPeriodOptions = useMemo(() => [...getPayPeriodOptions()].reverse(), []);
  const currentPeriodValue = useMemo(() => {
    const p = getCurrentPayPeriod();
    return `${toDateOnlyString(p.start)}|${toDateOnlyString(p.end)}`;
  }, []);
  const weekOptions = useMemo(() => getWeekOptions(), []);
  const currentWeekValue = weekOptions[0].value;

  // A list, so one report can cover a team. Null until the signed-in user
  // arrives below - an empty array is a real answer ("nobody"), and the two
  // have to stay tellable apart or the page would never open on anybody.
  const [employeeIds, setEmployeeIds] = useState(null);
  // Opens on the week that last finished: the report goes out weekly, after
  // the week it covers is over.
  const [dateMode, setDateMode] = useState('week'); // 'week' | 'payPeriod' | 'custom'
  const [weekFilter, setWeekFilter] = useState(weekOptions[1].value);
  const [periodFilter, setPeriodFilter] = useState(currentPeriodValue);
  const [customRange, setCustomRange] = useState(null);

  const quickDateRanges = useMemo(() => getQuickDateRanges(), []);

  // Open on the signed-in user. Only until they pick somebody else - the guard
  // stops a late-arriving /employees/me from yanking the page back to them.
  const meId = auth.employee?.id || null;
  useEffect(() => {
    if (meId && employeeIds === null) setEmployeeIds([meId]);
  }, [meId, employeeIds]);

  // What the rest of the page works from: never null, so nothing below has to
  // ask whether the roster has arrived yet.
  const selectedEmployeeIds = useMemo(() => employeeIds || [], [employeeIds]);

  const { start, end } = useMemo(() => {
    if (dateMode === 'custom') {
      if (Array.isArray(customRange) && customRange[0] && customRange[1]) {
        return { start: toDateOnlyString(customRange[0]), end: toDateOnlyString(customRange[1]) };
      }
      return { start: '', end: '' };
    }
    const [s, e] = String((dateMode === 'week' ? weekFilter : periodFilter) || '').split('|');
    return { start: s || '', end: e || '' };
  }, [dateMode, customRange, weekFilter, periodFilter]);

  const reportQuery = useHoursBreakdown({ employeeIds: selectedEmployeeIds, start, end });
  const report = reportQuery.data || null;

  const employeeOptions = useMemo(() => {
    const rows = employeesQuery.data?.employees || [];
    return rows.map((emp) => ({
      label: emp.isActive ? emp.name : `${emp.name} (inactive)`,
      value: emp.id,
    }));
  }, [employeesQuery.data]);

  // The people this report actually covers, as the server reported them back.
  // Falls back to the one-employee spelling for a page talking to a server that
  // predates the list.
  const reportPeople = useMemo(() => {
    if (Array.isArray(report?.employees)) return report.employees;
    return report?.employee ? [report.employee] : [];
  }, [report]);

  const isMultiEmployee = reportPeople.length > 1;

  // One phrase naming them, for headings and prose. Beyond a couple of names a
  // list stops reading as a name and starts reading as a list, so it counts.
  const peopleLabel = useMemo(() => {
    const names = reportPeople.map((e) => e.name).filter(Boolean);
    if (names.length === 0) return 'Unknown employee';
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} and ${names[1]}`;
    return `${names.length} employees`;
  }, [reportPeople]);

  // Named in full wherever there is room for it, however many there are.
  const peopleList = useMemo(
    () => reportPeople.map((e) => e.name).filter(Boolean).join(', '),
    [reportPeople]
  );

  // Only the ones TimeCamp could not identify, since only they are inexact.
  const emailMatched = useMemo(
    () => reportPeople.filter((e) => e.matchedBy === 'email').map((e) => e.name),
    [reportPeople]
  );

  // --- Task check state ----------------------------------------------------

  // Held as the set that is OFF, so a task that only appears in the next pull
  // arrives checked, and a saved filter keeps meaning "not these" however the
  // task list moves underneath it.
  const [excludedTaskIds, setExcludedTaskIds] = useState(() => new Set());

  const settingsQuery = useHoursBreakdownSettings();
  const saveSettings = useSaveHoursBreakdownSettings();

  const savedExcluded = useMemo(
    () => settingsQuery.data?.settings?.excludedTasks || [],
    [settingsQuery.data]
  );

  // Labels are stored beside the ids so a saved exclusion for a task that is
  // not in this date range can still be named in the settings panel.
  const savedLabels = useMemo(() => {
    const map = new Map();
    for (const item of savedExcluded) if (item.label) map.set(item.taskId, item.label);
    return map;
  }, [savedExcluded]);

  // A stable identity for "what is saved", so the effect below re-applies the
  // defaults when they load or change rather than on every render.
  const savedKey = useMemo(
    () => savedExcluded.map((t) => t.taskId).sort().join(','),
    [savedExcluded]
  );
  const currentKey = useMemo(() => [...excludedTaskIds].sort().join(','), [excludedTaskIds]);
  const hasUnsavedChanges = savedKey !== currentKey;

  // The saved filter is the starting point for every report: re-applied when
  // the settings arrive and again whenever the people or date range change,
  // since an ad-hoc tweak was about the report that was on screen at the time.
  const reportKey = `${selectedEmployeeIds.join(',')}|${start}|${end}`;
  useEffect(() => {
    setExcludedTaskIds(new Set(savedKey ? savedKey.split(',') : []));
  }, [reportKey, savedKey]);

  const allTasks = useMemo(() => {
    const rows = report?.tasks || [];
    const total = rows.reduce((sum, t) => sum + (Number(t.hours) || 0), 0);
    return rows.map((task) => ({
      ...task,
      display: taskDisplay(task),
      share: total > 0 ? ((Number(task.hours) || 0) / total) * 100 : 0,
      included: !excludedTaskIds.has(task.taskId),
    }));
  }, [report, excludedTaskIds]);

  const includedTaskIds = useMemo(
    () => new Set(allTasks.filter((t) => t.included).map((t) => t.taskId)),
    [allTasks]
  );

  const toggleTask = (taskId) => {
    setExcludedTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const checkAll = () => setExcludedTaskIds(new Set());
  const clearAll = () => setExcludedTaskIds(new Set(allTasks.map((t) => t.taskId)));
  const invertAll = () =>
    setExcludedTaskIds(new Set(allTasks.filter((t) => t.included).map((t) => t.taskId)));

  // Saved exclusions for tasks the current pull knows nothing about - almost
  // always a task from another project or another month. They stay in the saved
  // set (dropping them would quietly undo the filter next time that task turns
  // up) and are listed in the panel so they can be removed deliberately.
  const offRangeExclusions = useMemo(() => {
    const here = new Set(allTasks.map((t) => t.taskId));
    return [...excludedTaskIds]
      .filter((taskId) => !here.has(taskId))
      .map((taskId) => ({ taskId, label: savedLabels.get(taskId) || `Task ${taskId}` }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [excludedTaskIds, allTasks, savedLabels]);

  // Persist the current checkboxes. Labels are taken from this pull where it has
  // them and carried over from the saved set otherwise, so an off-range
  // exclusion keeps its name instead of decaying to a bare id.
  const handleSaveDefaults = () => {
    const labelHere = new Map(allTasks.map((t) => [t.taskId, t.display]));
    saveSettings.mutate({
      excludedTasks: [...excludedTaskIds].map((taskId) => ({
        taskId,
        label: labelHere.get(taskId) || savedLabels.get(taskId) || null,
      })),
    });
  };

  const handleRevert = () => setExcludedTaskIds(new Set(savedKey ? savedKey.split(',') : []));
  const handleClearDefaults = () => saveSettings.mutate({ clear: true });

  // --- Derived report ------------------------------------------------------

  const entries = useMemo(
    () => (report?.entries || []).filter((e) => includedTaskIds.has(e.taskId)),
    [report, includedTaskIds]
  );

  // Only the total is left now that the tile strip is gone: it is what the
  // section notes quote and what the By Task shares are taken against.
  const totals = useMemo(
    () => ({ hours: entries.reduce((sum, e) => sum + (Number(e.hours) || 0), 0) }),
    [entries]
  );

  // `share` on a task is its slice of everything tracked; the breakdown wants
  // its slice of what is actually being shown, so the two differ as soon as
  // anything is filtered out and the pie has to use the latter to reach 100%.
  const includedTasks = useMemo(
    () => allTasks
      .filter((t) => t.included)
      .map((t) => ({
        ...t,
        shareOfShown: totals.hours > 0 ? ((Number(t.hours) || 0) / totals.hours) * 100 : 0,
      })),
    [allTasks, totals.hours]
  );

  // Grouped one level up from the task: "Application Development" rather than
  // the root project it sits under. That is the level the work is actually
  // organised at - a root is too coarse to say anything (nearly everything is
  // product development) and a task is too fine to see a shape in.
  //
  // A task with no parent is its own group. It is already at parent level, and
  // filing it under "No parent" would invent a bucket that means nothing.
  const byParent = useMemo(() => {
    const map = new Map();
    for (const entry of entries) {
      const key = entry.parentTaskName || entry.taskName || 'No task';
      const row = map.get(key) || { parent: key, hours: 0, entries: 0 };
      row.hours += Number(entry.hours) || 0;
      row.entries += 1;
      map.set(key, row);
    }
    const total = [...map.values()].reduce((sum, r) => sum + r.hours, 0);
    return [...map.values()]
      .map((r) => ({ ...r, share: total > 0 ? (r.hours / total) * 100 : 0 }))
      .sort((a, b) => b.share - a.share);
  }, [entries]);

  // --- AI work-type view ---------------------------------------------------

  const classifyStatus = useHoursBreakdownClassifyStatus();
  const classify = useClassifyHoursBreakdown();
  const [openBucket, setOpenBucket] = useState(null);

  const classified = classify.data || null;

  const setOverride = useSetHoursBreakdownOverride();

  // Corrections made since this run, so a reclassification shows up immediately
  // instead of waiting for a re-run. Keyed exactly as the server keys them, so
  // one correction moves every row that says the same thing. The server applies
  // the stored ones itself, which is what makes them survive a refresh.
  const [overrides, setOverrides] = useState(() => new Map());

  // The run's verdicts with this session's corrections laid over them. Everything
  // downstream - the chart, the list and the entries table - reads this, so the
  // three can never disagree about where an entry sits.
  const effectiveVerdicts = useMemo(() => {
    const rows = classified?.entries || [];
    if (overrides.size === 0) return rows;
    return rows.map((row) => {
      const bucket = row.overrideKey ? overrides.get(row.overrideKey) : undefined;
      if (!bucket || bucket === row.bucket) return row;
      return { ...row, bucket, confidence: null, reason: 'Set by hand.', source: 'override' };
    });
  }, [classified, overrides]);

  /*
    The verdicts describe one set of people, over one date range, for one set of
    counted tasks. When any of the three moves, the old answer is about a
    different report and has to go rather than sit there looking current.

    The task filter belongs in that list as much as the other two, and is the
    easiest of them to miss: only the counted tasks are sent to be classified,
    so unchecking one leaves its hours in the Work Type chart, the bucket lists
    and the exported PDF until somebody thinks to re-run - and checking one back
    on leaves its hours out of them. Everything else on the page re-totals from
    the checkboxes on the spot, which is exactly what makes a stale chart beside
    them so easy to believe.

    Dropping the run rather than trying to filter it locally, because the run is
    the only thing that knows which entries it was given. Re-running is cheap -
    the answers are cached server-side - so the cost of being honest here is a
    button press.
  */
  useEffect(() => {
    classify.reset();
    setOpenBucket(null);
    setOverrides(new Map());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportKey, currentKey]);

  // Rows for the four buckets plus whatever could not be placed, in the order
  // the buckets are defined so the colours do not move between runs.
  // The four real categories only. Work that could not be placed is not a fifth
  // kind of work, and leaving it in the chart would make every share a share of
  // "classified plus whatever we gave up on" - so the percentages here are of
  // the work that actually got a category. What was left out is stated under the
  // list instead, where it can be gone through and corrected.
  const workRows = useMemo(() => {
    if (!classified) return [];
    const order = (classified.buckets || []).map((b) => b.key);
    const totals = new Map();
    for (const entry of effectiveVerdicts) {
      const row = totals.get(entry.bucket) || { hours: 0, entries: 0 };
      row.hours += Number(entry.hours) || 0;
      row.entries += 1;
      totals.set(entry.bucket, row);
    }
    const grand = order.reduce((sum, key) => sum + (totals.get(key)?.hours || 0), 0);
    return order
      .filter((key) => totals.has(key))
      .map((key) => {
        const row = totals.get(key);
        return {
          key,
          label: classified.labels?.[key] || key,
          hours: Math.round((row.hours + Number.EPSILON) * 100) / 100,
          entries: row.entries,
          share: grand > 0 ? (row.hours / grand) * 100 : 0,
        };
      });
  }, [classified, effectiveVerdicts]);

  // Kept out of the chart, but counted, so it is never quietly dropped.
  const unplaced = useMemo(() => {
    if (!classified) return null;
    const rows = effectiveVerdicts.filter((e) => e.bucket === classified.unclassifiedKey);
    return {
      entries: rows.length,
      hours: Math.round((rows.reduce((sum, e) => sum + (Number(e.hours) || 0), 0) + Number.EPSILON) * 100) / 100,
    };
  }, [classified, effectiveVerdicts]);

  // How often the task label would have given the wrong answer. This is the
  // whole justification for asking a model instead of reading the task name, so
  // the page states it rather than leaving it to be taken on trust.
  const reclassified = useMemo(() => {
    if (!classified) return null;
    const rows = effectiveVerdicts.filter(
      (e) => /develop/i.test(`${e.parentTaskName || ''} ${e.taskName || ''}`)
        && e.bucket !== 'new_products'
    );
    return {
      count: rows.length,
      hours: Math.round((rows.reduce((sum, e) => sum + (Number(e.hours) || 0), 0) + Number.EPSILON) * 100) / 100,
    };
  }, [classified, effectiveVerdicts]);

  const entriesInBucket = useMemo(() => {
    // No guard on `classified` needed: without a run there are no verdicts.
    if (!openBucket) return [];
    return effectiveVerdicts
      .filter((e) => e.bucket === openBucket)
      .sort((a, b) => (Number(b.hours) || 0) - (Number(a.hours) || 0));
  }, [effectiveVerdicts, openBucket]);

  const runClassify = () => {
    if (needsRangeForClassify) return;
    classify.mutate({
      employeeIds: selectedEmployeeIds,
      start,
      end,
      excludedTaskIds: [...excludedTaskIds],
    });
  };

  // Typed one per line; a leading dash, asterisk or bullet is dropped so pasting
  // an existing list does not print two bullets on each line.
  const [highlightsText, setHighlightsText] = useState('');
  const highlights = useMemo(
    () => highlightsText
      .split(/\r?\n/)
      .map((line) => line.replace(/^\s*(?:[-*\u2022]\s*)?/, '').trim())
      .filter(Boolean),
    [highlightsText]
  );

  // Exports what is on screen, corrections included - `workRows` is the same
  // value the chart renders, so the PDF cannot disagree with it.
  const exportPdf = () => {
    if (!classified) return;
    exportWorkTypePdf({
      rows: workRows,
      // The same counted tasks the run was given, so the list matches the chart.
      tasks: includedTasks.map((t) => ({ label: t.display, hours: t.hours, share: t.shareOfShown })),
      highlights,
      meta: {
        employeeName: peopleList || peopleLabel,
        periodLabel,
        start,
        end,
      },
    });
  };

  // --- Entry list ----------------------------------------------------------

  // Joining the classifier's verdicts back onto the report's entries.
  //
  // By TimeCamp entry id where there is one. The classifier's own `ref` falls
  // back to a positional key, which indexes its own filtered list and would line
  // up with the wrong row here - so the fallback is the entry's content instead,
  // which is stable across both calls.
  const entryKey = (entry) =>
    entry.id ? `id:${entry.id}` : `k:${entry.date}|${entry.taskId}|${entry.hours}|${entry.description || ''}`;

  const verdictByEntry = useMemo(() => {
    const map = new Map();
    for (const row of effectiveVerdicts) map.set(entryKey(row), row);
    return map;
  }, [effectiveVerdicts]);

  // Entries carrying whatever the classifier said about them, so the table, the
  // search and the work-type filter all read from one shape.
  const decoratedEntries = useMemo(
    () => entries.map((entry) => {
      const verdict = verdictByEntry.get(entryKey(entry));
      return {
        ...entry,
        bucket: verdict ? verdict.bucket : null,
        bucketLabel: verdict ? (classified?.labels?.[verdict.bucket] || verdict.bucket) : null,
        confidence: verdict ? verdict.confidence : null,
        reason: verdict ? verdict.reason : null,
        verdictSource: verdict ? verdict.source : null,
        overrideKey: verdict ? verdict.overrideKey : null,
      };
    }),
    [entries, verdictByEntry, classified]
  );

  // Reclassify one entry. Optimistic: the row (and every other row saying the
  // same thing) moves at once, and is put back if the write fails - a correction
  // that silently did not save would be worse than one that visibly failed.
  const reclassify = (entry, bucket) => {
    if (!entry.overrideKey) return;
    const previous = overrides.get(entry.overrideKey);
    setOverrides((prev) => {
      const next = new Map(prev);
      if (bucket) next.set(entry.overrideKey, bucket);
      else next.delete(entry.overrideKey);
      return next;
    });
    setOverride.mutate(
      {
        taskName: entry.taskName,
        parentTaskName: entry.parentTaskName,
        description: entry.description,
        bucket: bucket || null,
      },
      {
        onError: () => {
          setOverrides((prev) => {
            const next = new Map(prev);
            if (previous) next.set(entry.overrideKey, previous);
            else next.delete(entry.overrideKey);
            return next;
          });
          showToast('error', 'That work type change could not be saved.');
        },
      }
    );
  };

  const [entrySearch, setEntrySearch] = useState('');
  const [bucketFilter, setBucketFilter] = useState(null);
  const [taskFilter, setTaskFilter] = useState(null);

  // Only offer work types that are actually on screen, plus whatever is
  // currently selected so the filter cannot silently drop its own value.
  const bucketFilterOptions = useMemo(() => {
    if (!classified) return [];
    const present = new Map();
    for (const entry of decoratedEntries) {
      if (!entry.bucket) continue;
      present.set(entry.bucket, entry.bucketLabel || entry.bucket);
    }
    const options = [...present.entries()].map(([value, label]) => ({ value, label }));
    // Entries the run never saw - a task excluded at classify time, or a pull
    // that has moved on since. Worth being able to isolate.
    if (decoratedEntries.some((e) => !e.bucket)) {
      options.push({ value: '__none__', label: 'Not classified' });
    }
    return options;
  }, [decoratedEntries, classified]);

  // Only the tasks actually present in the rows on screen, named the same way
  // the Task column names them so the two cannot read differently.
  const taskFilterOptions = useMemo(() => {
    const present = new Map();
    for (const entry of decoratedEntries) {
      if (!present.has(entry.taskId)) present.set(entry.taskId, taskDisplay(entry));
    }
    return [...present.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [decoratedEntries]);

  const filteredEntries = useMemo(() => {
    const q = entrySearch.trim().toLowerCase();
    return decoratedEntries.filter((entry) => {
      if (bucketFilter === '__none__' && entry.bucket) return false;
      if (bucketFilter && bucketFilter !== '__none__' && entry.bucket !== bucketFilter) return false;
      if (taskFilter && entry.taskId !== taskFilter) return false;
      if (!q) return true;
      return [entry.description, taskDisplay(entry), entry.bucketLabel, entry.reason]
        .some((field) => String(field || '').toLowerCase().includes(q));
    });
  }, [decoratedEntries, entrySearch, bucketFilter, taskFilter]);

  const entrySort = useTableSort('date', 'asc');
  const sortedEntries = useMemo(
    () =>
      sortRows(filteredEntries, entrySort.sortKey, entrySort.sortDir, {
        date: (e) => e.date,
        employee: (e) => String(e.employeeName || ''),
        task: (e) => taskDisplay(e),
        description: (e) => e.description,
        workType: (e) => e.bucketLabel,
        hours: (e) => Number(e.hours) || 0,
      }),
    [filteredEntries, entrySort.sortKey, entrySort.sortDir]
  );

  // The table foots the rows it is showing, not the report - a total that
  // ignores the filter above it is just wrong.
  const shownHours = useMemo(
    () => sortedEntries.reduce((sum, e) => sum + (Number(e.hours) || 0), 0),
    [sortedEntries]
  );

  const entryFilterActive = Boolean(entrySearch.trim() || bucketFilter || taskFilter);
  const clearEntryFilters = () => {
    setEntrySearch('');
    setBucketFilter(null);
    setTaskFilter(null);
  };

  // --- Render --------------------------------------------------------------

  const periodLabel = useMemo(() => {
    if (!start || !end) return '';
    const s = new Date(`${start}T00:00:00`);
    const e = new Date(`${end}T00:00:00`);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return '';
    return formatPeriodLabel(s, e);
  }, [start, end]);

  const pulledAt = fmtPulledAt(report?.pulledAt);
  const needsRange = !start || !end;
  const needsRangeForClassify = needsRange || selectedEmployeeIds.length === 0;

  const savedAtLabel = useMemo(() => {
    const iso = settingsQuery.data?.savedAt;
    if (!iso) return null;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
  }, [settingsQuery.data]);

  // What the collapsed panel says about itself, so the filter is never silently
  // in effect - a report quietly missing 9 hours is the failure mode here.
  const settingsNote = useMemo(() => {
    const excludedCount = excludedTaskIds.size;
    const base = excludedCount === 0
      ? 'Counting every task'
      : `${excludedCount} task${excludedCount === 1 ? '' : 's'} excluded`;
    if (hasUnsavedChanges) return `${base} · unsaved`;
    if (excludedCount === 0) return base;
    return `${base} · saved default`;
  }, [excludedTaskIds, hasUnsavedChanges]);

  const sortableHeader = (key, label, align) => (
    <SortableTh sort={entrySort} sortKey={key} align={align === 'end' ? 'right' : undefined}>
      {label}
    </SortableTh>
  );

  return (
    <div className="bdc-page hours-breakdown">
      <PageHeader
        crumbs={[
          { label: 'Home', to: '/' },
          { label: 'Admin', to: '/admin' },
          { label: 'Configuration', to: '/admin/configuration' },
          { label: 'Hours Breakdown' },
        ]}
        icon="bi-clock-history"
        title="Hours Breakdown"
        note={periodLabel || null}
        actions={(
          <button
            type="button"
            className="bdc-btn bdc-btn--primary"
            onClick={() => reportQuery.refetch()}
            disabled={needsRange || reportQuery.isFetching}
          >
            <i className="bi bi-arrow-clockwise" aria-hidden="true" />
            {reportQuery.isFetching ? 'Pulling…' : 'Refresh'}
          </button>
        )}
      />

      <FilterToolbar>
        <FilterField label="Employee" basis={260} max={340}>
          <CheckPicker
            data={employeeOptions}
            countable={false}
            renderValue={renderEmployeeSelection(employeeOptions)}
            value={selectedEmployeeIds}
            onChange={(value) => setEmployeeIds(value || [])}
            cleanable={false}
            block
            sticky
            placeholder={employeesQuery.isLoading ? 'Loading…' : 'Select employees'}
          />
        </FilterField>

        <FilterField label="Date">
          <ButtonGroup>
            <Button
              appearance={dateMode === 'week' ? 'primary' : 'default'}
              onClick={() => setDateMode('week')}
            >
              Week
            </Button>
            <Button
              appearance={dateMode === 'payPeriod' ? 'primary' : 'default'}
              onClick={() => setDateMode('payPeriod')}
            >
              Pay Period
            </Button>
            <Button
              appearance={dateMode === 'custom' ? 'primary' : 'default'}
              onClick={() => setDateMode('custom')}
            >
              Custom Range
            </Button>
          </ButtonGroup>
        </FilterField>

        {dateMode === 'week' ? (
          <FilterField label="Week" basis={240} max={320}>
            <PayPeriodSelect
              data={weekOptions}
              value={weekFilter}
              currentValue={currentWeekValue}
              onChange={(value) => value && setWeekFilter(String(value))}
              block
            />
          </FilterField>
        ) : dateMode === 'payPeriod' ? (
          <FilterField label="Pay Period" basis={240} max={320}>
            <PayPeriodSelect
              data={payPeriodOptions}
              value={periodFilter}
              currentValue={currentPeriodValue}
              onChange={(value) => value && setPeriodFilter(String(value))}
              block
            />
          </FilterField>
        ) : (
          <FilterField label="Date Range" basis={240} minWidth={260}>
            <DateRangePicker
              value={customRange}
              onChange={setCustomRange}
              placeholder="Select date range"
              format="yyyy-MM-dd"
              block
              ranges={quickDateRanges}
            />
          </FilterField>
        )}

        <FilterField label={pulledAt ? `Pulled ${pulledAt}` : 'Live from TimeCamp'}>
          <span className="hours-breakdown__pullnote">
            {reportQuery.isFetching ? 'Reading TimeCamp…' : `${report?.entries?.length ?? 0} entries pulled`}
          </span>
        </FilterField>
      </FilterToolbar>

      {reportQuery.isError ? (
        <Message type="error" showIcon header="Could not pull hours">
          {String(reportQuery.error?.message || reportQuery.error)}
        </Message>
      ) : null}

      {emailMatched.length > 0 ? (
        <Message type="warning" showIcon>
          {emailMatched.join(', ')} {emailMatched.length === 1 ? 'has' : 'have'} no TimeCamp user id
          on file, so those entries were matched by email address. Set the TimeCamp id on the
          employee record to make this exact.
        </Message>
      ) : null}

      {report && report.taskTreeResolved === false ? (
        <Message type="warning" showIcon>
          TimeCamp&apos;s task list could not be read, so hours could not be grouped by parent.
          The task breakdown and totals below are still accurate.
        </Message>
      ) : null}

      {needsRange ? (
        <Message type="info" showIcon>Pick a date range to pull hours.</Message>
      ) : reportQuery.isLoading ? (
        <Placeholder.Graph active height={320} />
      ) : !report ? null : allTasks.length === 0 ? (
        <Message type="info" showIcon>
          No TimeCamp entries for {peopleLabel} in this range.
        </Message>
      ) : (
        <>
          <Section
            title="Report Settings"
            collapsible
            note={settingsNote}
          >
            <div className="hours-breakdown__settings">
              <div className="hours-breakdown__settings-bar">
                <span className="hours-breakdown__settings-lead">
                  Counted tasks
                  <span className="bdc-count">{includedTasks.length}</span>
                </span>
                <div className="bdc-segment" role="group" aria-label="Task selection">
                  <button type="button" className="bdc-btn bdc-btn--sm" onClick={checkAll}>All</button>
                  <button type="button" className="bdc-btn bdc-btn--sm" onClick={clearAll}>None</button>
                  <button type="button" className="bdc-btn bdc-btn--sm" onClick={invertAll}>Invert</button>
                </div>
              </div>

              <ul className="hours-breakdown__tasks">
                {allTasks.map((task) => (
                  <li key={task.taskId}>
                    <label className={`hours-breakdown__task${task.included ? '' : ' is-off'}`}>
                      <input
                        type="checkbox"
                        checked={task.included}
                        onChange={() => toggleTask(task.taskId)}
                      />
                      <span className="hours-breakdown__task-name" title={task.display}>
                        {task.display}
                      </span>
                      <span className="hours-breakdown__task-hours bdc-num">
                        {fmtHours(task.hours)} h
                      </span>
                      {/* The bar is a second read of the same number beside it,
                          never the only one - it carries no meaning alone. */}
                      <span className="hours-breakdown__task-bar" aria-hidden="true">
                        <span style={{ width: `${Math.max(2, task.share)}%` }} />
                      </span>
                      <span className="hours-breakdown__task-share bdc-num">{fmtPct(task.share)}</span>
                    </label>
                  </li>
                ))}
              </ul>

              {offRangeExclusions.length > 0 ? (
                <div className="hours-breakdown__offrange">
                  <div className="hours-breakdown__offrange-label">
                    Also excluded, but not logged in this range
                  </div>
                  <div className="hours-breakdown__chips">
                    {offRangeExclusions.map((task) => (
                      <button
                        key={task.taskId}
                        type="button"
                        className="hours-breakdown__chip"
                        onClick={() => toggleTask(task.taskId)}
                        title="Stop excluding this task"
                      >
                        {task.label}
                        <i className="bi bi-x-lg" aria-hidden="true" />
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="hours-breakdown__settings-foot">
                <span className="hours-breakdown__settings-state">
                  {saveSettings.isError ? (
                    <span className="bdc-text-bad">
                      Could not save: {String(saveSettings.error?.message || saveSettings.error)}
                    </span>
                  ) : hasUnsavedChanges ? (
                    'Unsaved changes. Saving makes this the starting point for every report.'
                  ) : savedKey ? (
                    `Saved${savedAtLabel ? ` ${savedAtLabel}` : ''}. Applied to every report you open.`
                  ) : (
                    'No saved default. Every task is counted until you save one.'
                  )}
                </span>
                <div className="hours-breakdown__settings-actions">
                  {savedKey ? (
                    <button
                      type="button"
                      className="bdc-linkbtn bdc-linkbtn--danger"
                      onClick={handleClearDefaults}
                      disabled={saveSettings.isPending}
                    >
                      Clear default
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="bdc-btn bdc-btn--sm"
                    onClick={handleRevert}
                    disabled={!hasUnsavedChanges || saveSettings.isPending}
                  >
                    Revert
                  </button>
                  <button
                    type="button"
                    className="bdc-btn bdc-btn--sm bdc-btn--primary"
                    onClick={handleSaveDefaults}
                    disabled={!hasUnsavedChanges || saveSettings.isPending}
                  >
                    <i className="bi bi-bookmark-check" aria-hidden="true" />
                    {saveSettings.isPending ? 'Saving…' : 'Save as default'}
                  </button>
                </div>
              </div>
            </div>
          </Section>

          <section className="bdc-section">
            <div className="bdc-section__head">
              <div className="bdc-section__heading">
                <h2 className="bdc-section__title">Breakdown</h2>
                <span className="bdc-section__note">{fmtHours(totals.hours)} h shown</span>
              </div>
            </div>

            <div className="hours-breakdown__charts">
              <PiePanel
                title="By Parent"
                data={byParent}
                labelKey="parent"
                valueKey="share"
                valueDisplay="percent"
                renderTable={() => (
                  <table className="table table-sm align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Parent</th>
                        <th className="text-end">Entries</th>
                        <th className="text-end">Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byParent.map((p) => (
                        <tr key={p.parent}>
                          <td>{p.parent}</td>
                          <td className="text-end">{p.entries}</td>
                          <td className="text-end bdc-num">{fmtPct(p.share)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              />

              <PiePanel
                title="By Task"
                data={includedTasks}
                labelKey="display"
                valueKey="shareOfShown"
                valueDisplay="percent"
                renderTable={() => (
                  <table className="table table-sm align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Task</th>
                        <th className="text-end">Entries</th>
                        <th className="text-end">Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {includedTasks.map((t) => (
                        <tr key={t.taskId}>
                          <td>{t.display}</td>
                          <td className="text-end">{t.entries}</td>
                          <td className="text-end bdc-num">{fmtPct(t.shareOfShown)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              />
            </div>
          </section>

          <section className="bdc-section">
            <div className="bdc-section__head">
              <div className="bdc-section__heading">
                <h2 className="bdc-section__title">Work Type</h2>
                {classified ? <span className="bdc-count">{workRows.length}</span> : null}
                <span className="bdc-section__note">
                  {classified
                    ? 'Classified from what was written, not from the task it was logged against.'
                    : 'Ask the AI to sort these hours into four kinds of work.'}
                </span>
              </div>
              <div className="hours-breakdown__workactions">
                {classified ? (
                  <button
                    type="button"
                    className="bdc-btn bdc-btn--sm"
                    onClick={exportPdf}
                    title="Download this chart and breakdown as a PDF"
                  >
                    <i className="bi bi-file-earmark-pdf" aria-hidden="true" />
                    Export PDF
                  </button>
                ) : null}
                <button
                  type="button"
                  className="bdc-btn bdc-btn--sm bdc-btn--primary"
                  onClick={runClassify}
                  disabled={needsRangeForClassify || classify.isPending || classifyStatus.data?.ready === false}
                >
                  <i className="bi bi-stars" aria-hidden="true" />
                  {classify.isPending ? 'Classifying…' : classified ? 'Re-run' : 'Classify with AI'}
                </button>
              </div>
            </div>

            {classifyStatus.data && classifyStatus.data.ready === false ? (
              <Message type="info" showIcon>{classifyStatus.data.message}</Message>
            ) : classify.isError ? (
              <Message type="error" showIcon header="Could not classify">
                {String(classify.error?.message || classify.error)}
              </Message>
            ) : classify.isPending ? (
              <Placeholder.Graph active height={220} />
            ) : !classified ? (
              <p className="hours-breakdown__workblurb">
                Every entry is read on its own and sorted into <strong>development of new
                products</strong>, <strong>maintenance and improvement of existing products</strong>, <strong>IT and
                help</strong>, or <strong>roadmap and planning</strong>. Task names are deliberately
                not trusted — work logged against a “Development” task often turns out to be
                maintenance, and IT work is usually buried in “Admin”. Anything the description does
                not settle is left unclassified rather than guessed at. Only the tasks you are
                counting are sent, and answers are cached, so re-running is cheap.
              </p>
            ) : (
              <>
                {classified.configWarning ? (
                  <Message type="warning" showIcon>{classified.configWarning}</Message>
                ) : null}

                <div className="hours-breakdown__highlights">
                  <label className="hours-breakdown__highlightslabel" htmlFor="hours-breakdown-highlights">
                    Highlights
                    <span>One per line. Shown as bullet points at the top of the PDF.</span>
                  </label>
                  <Input
                    id="hours-breakdown-highlights"
                    as="textarea"
                    rows={4}
                    value={highlightsText}
                    onChange={setHighlightsText}
                  />
                </div>

                <div className="hours-breakdown__charts">
                  <PiePanel
                    title="By Work Type"
                    data={workRows}
                    labelKey="label"
                    valueKey="share"
                    valueDisplay="percent"
                    maxSlices={5}
                    onSelect={(label) => {
                      const row = workRows.find((r) => r.label === label);
                      setOpenBucket((cur) => (row && cur === row.key ? null : row?.key || null));
                    }}
                    renderTable={() => (
                      <table className="table table-sm align-middle mb-0">
                        <thead>
                          <tr>
                            <th>Work type</th>
                            <th className="text-end">Entries</th>
                            <th className="text-end">Share</th>
                          </tr>
                        </thead>
                        <tbody>
                          {workRows.map((r) => (
                            <tr key={r.key}>
                              <td>{r.label}</td>
                              <td className="text-end">{r.entries}</td>
                              <td className="text-end bdc-num">{fmtPct(r.share)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  />

                  <div className="hours-breakdown__worklist">
                    <div className="hours-breakdown__worklist-head">
                      <span>Breakdown</span>
                      <span className="hours-breakdown__worklist-hint">Pick one to see its entries</span>
                    </div>
                    <div className="hours-breakdown__worklist-body">
                      {workRows.map((r) => (
                        <button
                          key={r.key}
                          type="button"
                          className={`hours-breakdown__workrow${openBucket === r.key ? ' is-open' : ''}`}
                          onClick={() => setOpenBucket((cur) => (cur === r.key ? null : r.key))}
                          aria-expanded={openBucket === r.key}
                        >
                          <span className="hours-breakdown__workrow-label">{r.label}</span>
                          <span className="hours-breakdown__workrow-bar" aria-hidden="true">
                            <span style={{ width: `${Math.max(2, r.share)}%` }} />
                          </span>
                          <span className="hours-breakdown__workrow-pct bdc-num">{fmtPct(r.share)}</span>
                          <i
                            className={`bi bi-chevron-${openBucket === r.key ? 'down' : 'right'}`}
                            aria-hidden="true"
                          />
                        </button>
                      ))}
                    </div>
                    <div className="hours-breakdown__worklist-foot">
                      {unplaced && unplaced.entries > 0 ? (
                        <div className="hours-breakdown__unplaced">
                          <i className="bi bi-dash-circle" aria-hidden="true" />{' '}
                          {unplaced.entries} entries ({fmtHours(unplaced.hours)} h) had no category
                          and are not in the chart.{' '}
                          <button
                            type="button"
                            className="bdc-linkbtn"
                            onClick={() => {
                              setBucketFilter(classified.unclassifiedKey);
                              setEntrySearch('');
                              setTaskFilter(null);
                            }}
                          >
                            Show them below
                          </button>
                        </div>
                      ) : null}
                      {reclassified && reclassified.count > 0 ? (
                        <>
                          <i className="bi bi-info-circle" aria-hidden="true" />{' '}
                          {reclassified.count} entries ({fmtHours(reclassified.hours)} h) on a
                          “development” task were not new-product work.
                        </>
                      ) : (
                        <>
                          <i className="bi bi-info-circle" aria-hidden="true" />{' '}
                          Every entry on a “development” task read as new-product work.
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {openBucket ? (
                  <div className="nsi-table-wrap">
                    <div className="bdc-table-head">
                      <div className="bdc-table-head__heading">
                        <h3 className="bdc-table-head__title">
                          {classified.labels?.[openBucket] || openBucket}
                        </h3>
                        <span className="bdc-count">{entriesInBucket.length}</span>
                      </div>
                      <button
                        type="button"
                        className="bdc-linkbtn"
                        onClick={() => setOpenBucket(null)}
                      >
                        Close
                      </button>
                    </div>
                    <table className="nsi-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Logged against</th>
                          <th>Description</th>
                          <th>Why here</th>
                          <th className="nsi-table__right">Hours</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entriesInBucket.map((entry, index) => (
                          <tr key={entry.ref || index}>
                            <td>{fmtDate(entry.date)}</td>
                            <td className="nsi-table__muted">{taskDisplay(entry)}</td>
                            <td className={entry.description ? undefined : 'nsi-table__muted'}>
                              {entry.description || 'No description'}
                            </td>
                            <td className="nsi-table__muted">
                              {/* Confidence rides with the reason so a weak call
                                  is visible where it matters, not in a legend. */}
                              {entry.source === 'rule' ? (
                                <span className="hours-breakdown__badge">rule</span>
                              ) : entry.confidence != null ? (
                                <span
                                  className={`hours-breakdown__badge${entry.confidence < 70 ? ' is-weak' : ''}`}
                                >
                                  {Math.round(entry.confidence)}%
                                </span>
                              ) : null}{' '}
                              {entry.reason}
                            </td>
                            <td className="nsi-table__right bdc-num">{fmtHours(entry.hours)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}

                <p className="hours-breakdown__workstats">
                  {classified.stats.entries} entries ·{' '}
                  {classified.stats.byRule} by rule ·{' '}
                  {classified.stats.fromModel} newly classified ·{' '}
                  {classified.stats.fromCache} from cache
                  {classified.stats.failed > 0 ? ` · ${classified.stats.failed} failed` : ''}
                </p>
              </>
            )}
          </section>

          <section className="bdc-section">
            <div className="nsi-table-wrap">
              <div className="bdc-table-head">
                <div className="bdc-table-head__heading">
                  <h3 className="bdc-table-head__title">Entries</h3>
                  <span className="bdc-count">{sortedEntries.length}</span>
                  {entryFilterActive ? (
                    <span className="bdc-table-head__note">of {decoratedEntries.length}</span>
                  ) : null}
                </div>
                <div className="bdc-table-head__actions hours-breakdown__entryfilters">
                  <input
                    type="search"
                    className="hours-breakdown__search"
                    placeholder="Search descriptions and tasks…"
                    value={entrySearch}
                    onChange={(e) => setEntrySearch(e.target.value)}
                    aria-label="Search entries"
                  />
                  {classified ? (
                    <SelectPicker
                      size="sm"
                      data={bucketFilterOptions}
                      value={bucketFilter}
                      onChange={setBucketFilter}
                      placeholder="All work types"
                      searchable={false}
                      style={{ width: 200 }}
                    />
                  ) : null}
                  {/* Searchable: a long range can carry dozens of tasks, and the
                      labels are long enough that scanning a plain list is slow. */}
                  <SelectPicker
                    size="sm"
                    data={taskFilterOptions}
                    value={taskFilter}
                    onChange={setTaskFilter}
                    placeholder="All tasks"
                    style={{ width: 220 }}
                  />
                  {entryFilterActive ? (
                    <button type="button" className="bdc-linkbtn" onClick={clearEntryFilters}>
                      Clear
                    </button>
                  ) : null}
                </div>
              </div>
              <table className="nsi-table">
                <thead>
                  <tr>
                    {sortableHeader('date', 'Date')}
                    {isMultiEmployee ? sortableHeader('employee', 'Employee') : null}
                    {sortableHeader('task', 'Task')}
                    {sortableHeader('description', 'Description')}
                    {sortableHeader('workType', 'Work Type')}
                    {sortableHeader('hours', 'Hours', 'end')}
                  </tr>
                </thead>
                <tbody>
                  {sortedEntries.length === 0 ? (
                    <tr>
                      <td colSpan={isMultiEmployee ? 6 : 5} className="nsi-table__empty">
                        {entryFilterActive
                          ? 'No entries match these filters.'
                          : 'Every task is unchecked — check one in Report Settings to see its entries.'}
                      </td>
                    </tr>
                  ) : (
                    sortedEntries.map((entry, index) => (
                      <tr key={entry.id || `${entry.date}-${entry.taskId}-${index}`}>
                        <td>{fmtDate(entry.date)}</td>
                        {isMultiEmployee ? (
                          <td className="nsi-table__muted">{entry.employeeName || '—'}</td>
                        ) : null}
                        <td>{taskDisplay(entry)}</td>
                        <td className={entry.description ? undefined : 'nsi-table__muted'}>
                          {entry.description || 'No description'}
                        </td>
                        <td>
                          {!classified ? (
                            <span className="nsi-table__muted">—</span>
                          ) : !entry.overrideKey ? (
                            /* No description means no key to file a correction
                               under, so this one cannot be moved by hand. */
                            <span className="nsi-table__muted" title="An entry with no description cannot be reclassified.">
                              {entry.bucketLabel || 'Not classified'}
                            </span>
                          ) : (
                            <span
                              className="hours-breakdown__worktype"
                              /* The reason is why this row is where it is; it
                                 belongs on the cell that makes the claim. */
                              title={
                                entry.verdictSource === 'override'
                                  ? 'Set by hand. Applies wherever this description appears.'
                                  : entry.verdictSource === 'rule'
                                    ? entry.reason || 'Assigned by rule.'
                                    : [entry.reason, entry.confidence != null ? `(${Math.round(entry.confidence)}% confident)` : '']
                                        .filter(Boolean).join(' ')
                              }
                            >
                              <select
                                className={`hours-breakdown__bucketselect${entry.verdictSource === 'override' ? ' is-set' : ''}`}
                                value={entry.bucket || ''}
                                onChange={(e) => reclassify(entry, e.target.value)}
                                aria-label="Work type"
                              >
                                {(classified.buckets || []).map((b) => (
                                  <option key={b.key} value={b.key}>{b.label}</option>
                                ))}
                                <option value={classified.unclassifiedKey}>
                                  {classified.labels?.[classified.unclassifiedKey] || 'Unclassified'}
                                </option>
                              </select>
                              {entry.verdictSource === 'override' ? (
                                <button
                                  type="button"
                                  className="hours-breakdown__worktype-undo"
                                  onClick={() => reclassify(entry, null)}
                                  title="Undo this correction and use the AI's answer"
                                  aria-label="Undo correction"
                                >
                                  <i className="bi bi-arrow-counterclockwise" aria-hidden="true" />
                                </button>
                              ) : entry.confidence != null && entry.confidence < 70 ? (
                                <i
                                  className="bi bi-question-circle hours-breakdown__worktype-weak"
                                  aria-label="Low confidence"
                                />
                              ) : null}
                            </span>
                          )}
                        </td>
                        <td className="nsi-table__right bdc-num">{fmtHours(entry.hours)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                {sortedEntries.length > 0 ? (
                  <tfoot>
                    <tr>
                      {/* Everything left of Hours, which is one column wider
                          when the report names who logged each entry. */}
                      <td colSpan={isMultiEmployee ? 5 : 4}>
                        {entryFilterActive ? 'Total shown' : 'Total'}
                      </td>
                      <td className="nsi-table__right bdc-num">{fmtHours(shownHours)}</td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
